import React, { useState, useRef, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import "./MegaMenu.css";
import { useTranslation } from "react-i18next";

const MegaMenu = ({ categories }) => {
  const params = new URLSearchParams();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const triggerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  // Filter to get main categories and subcategories separately
  const mainCategories =
    categories?.data?.filter(
      (category) =>
        category.mainCategoryId === null && category.parentCategoryId === null
    ) || [];

  // Function to get subcategories based on mainCategoryId
  const getSubcategoriesByMainCategoryId = (mainCategoryId) => {
    if (!categories?.data) return [];
    
    // First, check if the main category has a subcategories array
    const mainCategory = categories.data.find(cat => cat._id === mainCategoryId);
    
    if (mainCategory && mainCategory.subcategories && mainCategory.subcategories.length > 0) {
      return mainCategory.subcategories;
    }
    
    // Try to find categories that have this specific mainCategoryId
    let filteredSubcategories = categories.data.filter(
      (category) => {
        // Handle case where mainCategoryId is an object with _id property
        const categoryMainId = category.mainCategoryId?._id || category.mainCategoryId;
        return String(categoryMainId) === String(mainCategoryId);
      }
    );
    
    // If no results, try with parentCategoryId
    if (filteredSubcategories.length === 0) {
      filteredSubcategories = categories.data.filter(
        (category) => {
          // Handle case where parentCategoryId is an object with _id property
          const categoryParentId = category.parentCategoryId?._id || category.parentCategoryId;
          return String(categoryParentId) === String(mainCategoryId);
        }
      );
    }
    
    // If still no results, return empty array (don't show dummy data for all categories)
    if (filteredSubcategories.length === 0) {
      return [];
    }
    return filteredSubcategories;
  };

  const handleCategoryClick = (categoryId, subcategoryId = "") => {
    if (subcategoryId) {
      // If subcategory is clicked, use subcategory ID as category_id
      params.set("category_id", subcategoryId);
    } else {
      // If main category is clicked, use main category ID
      params.set("category_id", categoryId);
    }
    navigate(`/products?${params.toString()}`, { replace: true });
    setOpenMenu(false);
  };

  const handleMouseEnter = () => {
    clearTimeout(timerRef.current);
    setOpenMenu(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setOpenMenu(false);
    }, 300);
  };

  return (
    <div
      className="menu-item"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpenMenu(false); }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') { clearTimeout(timerRef.current); setOpenMenu(false); triggerRef.current?.focus(); }
      }}
    >
      <button ref={triggerRef} type="button" className="menu-link" onClick={(event) => { clearTimeout(timerRef.current); setOpenMenu(previous => event.detail === 0 ? !previous : true); }} aria-expanded={openMenu} aria-controls="desktop-category-menu" style={{ border: 0, background: "transparent", font: "inherit" }}>
        {t("Categories")}
      </button>
      <div id="desktop-category-menu" className={`mega-menu ${openMenu ? "show" : ""}`} style={{display: openMenu ? 'flex' : 'none'}}>
        <div className="mega-menu-content">
          {/* Left Side - Main Category */}
          <div className="main-category-column">
            {mainCategories.length > 0 ? (
              mainCategories.map((mainCat) => (
                <a
                  key={mainCat._id}
                  className="main-category-item"
                  href={`/products?category_id=${encodeURIComponent(mainCat._id)}`}
                  onMouseEnter={() => setHoveredCategory(mainCat)}
                  onFocus={() => { clearTimeout(timerRef.current); setHoveredCategory(mainCat); }}
                  onClick={(event) => { event.preventDefault(); handleCategoryClick(mainCat._id); }}
                >
                  {mainCat.icon ? (
                    <img
                      src={`${mainCat.icon}`}
                      alt={`${mainCat.name} icon`}
                      className="category-icon-nav"
                    />
                  ) : null}
                  {mainCat.name}
                </a>
              ))
            ) : (
              <div className="no-categories">
                <p>Inga kategorier att visa just nu.</p>
              </div>
            )}
          </div>

          {/* Right Side - Subcategories */}
          <div className="subcategories-section">
            {hoveredCategory ? (
              <>
                {(() => {
                  const matchedSubcategories = getSubcategoriesByMainCategoryId(hoveredCategory._id);
                  return (
                    <>
                      {matchedSubcategories.length > 0 ? (
                        <div className="subcategories-grid">
                          {matchedSubcategories.map((sub, index) => (
                            <div key={sub._id} className="subcategory-item-grid">
                              <a
                                href={`/products?category_id=${encodeURIComponent(sub._id)}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick(hoveredCategory._id, sub._id);
                                }}
                                className="subcategory-link"
                              >
                                {sub.name}
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-subcategories">
                          <p>Inga underkategorier tillgängliga för {hoveredCategory.name}</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            ) : (
              <div className="subcategory-placeholder">
                <p>
                  Håll muspekaren över en kategori för att se underkategorier
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;
